// Observabilidad fase 1 (VLT-294) — reporte de errores PROPIO, sin SDK de
// vendor (misma filosofía que correo/SMS: el proveedor se decide después).
// Captura window.onerror + unhandledrejection, deduplica por mensaje, enmascara
// correos y postea al Worker (/api/telemetry/error) con el release del build.
// El Worker guarda en la tabla insert-only 0031; las alertas por umbral las
// evalúa telemetry-alerts.yml. Sentry/OTel = fase 2 (DSN del propietario).
import { hasBackend } from '@/lib/supabase';

/** Enmascara correos — un stack jamás debe exportar PII a la telemetría. */
export const scrubPii = (v: string): string =>
  v.replace(/[^\s@,;:"'()<>[\]]+@[^\s@,;:"'()<>[\]]+\.[A-Za-z]{2,}/g, '***@***');

const cap = (v: string | undefined | null, max: number): string | undefined =>
  v && v.trim() ? scrubPii(v.trim()).slice(0, max) : undefined;

export interface TelemetryReport {
  message: string;
  stack?: string;
  url?: string;
  release: string;
  level: 'error' | 'warning';
}

/** Normaliza un error crudo al reporte que viaja (puro — testeable). */
export const buildReport = (raw: unknown, pageUrl: string, release: string): TelemetryReport | null => {
  const err = raw instanceof Error ? raw : null;
  const message = cap(err ? `${err.name}: ${err.message}` : String(raw ?? ''), 500);
  if (!message) return null;
  return {
    message,
    stack: cap(err?.stack, 4000),
    url: cap(pageUrl, 300),
    release,
    level: 'error',
  };
};

const seen = new Set<string>();
const MAX_PER_SESSION = 10;

/** true si el reporte salió (nuevo y bajo el tope) — el envío jamás lanza. */
export const report = (raw: unknown): boolean => {
  const r = buildReport(raw, location.href, __BUILD_SHA__);
  if (!r || seen.has(r.message) || seen.size >= MAX_PER_SESSION) return false;
  seen.add(r.message);
  try {
    void fetch('/api/telemetry/error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(r),
      keepalive: true,
    }).catch(() => undefined);
  } catch { /* la telemetría jamás rompe la app */ }
  return true;
};

/** Sólo con backend real (o bandera de prueba e2e) — el modo demo no reporta. */
const enabled = (): boolean => {
  try {
    return hasBackend || localStorage.getItem('vaultex.telemetryTest') === '1';
  } catch {
    return hasBackend;
  }
};

export function initTelemetry(): void {
  if (!enabled()) return;
  window.addEventListener('error', (e) => void report(e.error ?? e.message));
  window.addEventListener('unhandledrejection', (e) => void report(e.reason));
}
